import { useState, useEffect, useRef } from "react";
import { useHome } from "../../data/content";

function RotatingTagline() {
  const home = useHome();
  const roles = home.heroRoles ?? [];
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("idle"); // 'idle' | 'rolling' | 'reset'
  const [width, setWidth] = useState(null);

  const currentMeasureRef = useRef(null);
  const nextMeasureRef    = useRef(null);

  const nextIndex = roles.length ? (index + 1) % roles.length : 0;

  // Set initial width after first render
  useEffect(() => {
    if (currentMeasureRef.current) {
      setWidth(currentMeasureRef.current.offsetWidth);
    }
  }, []);

  // When rolling starts, animate the container to the next word's width
  useEffect(() => {
    if (phase === "rolling" && nextMeasureRef.current) {
      setWidth(nextMeasureRef.current.offsetWidth);
    }
  }, [phase]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase("rolling");

      setTimeout(() => {
        setIndex((prev) => (prev + 1) % roles.length);
        setPhase("reset");

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPhase("idle");
          });
        });
      }, 500);
    }, 2300);

    return () => clearInterval(interval);
  }, [roles.length]);

  if (!roles.length) return null;

  const isRolling    = phase === "rolling";
  const hasTransition = phase !== "reset";

  return (
    <p className="font-sans text-lg text-text-muted-light dark:text-text-muted-dark mb-6">
      {home.heroTaglinePrefix}{" "}
      <span
        className="relative inline-block align-bottom"
        style={{
          height: "1.5em",
          overflowX: "clip",
          overflowY: "hidden",
          width: width != null ? `${width}px` : undefined,
          transition: hasTransition ? "width 0.5s cubic-bezier(0.4,0,0.2,1)" : "none",
        }}
      >
        {/* Hidden measuring spans — absolute so they don't affect layout */}
        <span
          ref={currentMeasureRef}
          className="absolute invisible font-serif italic font-bold whitespace-nowrap pointer-events-none"
        >
          {roles[index]}
        </span>
        <span
          ref={nextMeasureRef}
          className="absolute invisible font-serif italic font-bold whitespace-nowrap pointer-events-none"
        >
          {roles[nextIndex]}
        </span>

        {/* Current word — rolls upward and fades out */}
        <span
          className="absolute inset-0 flex items-center font-serif italic font-bold text-black dark:text-white underline decoration-1 underline-offset-4 whitespace-nowrap"
          style={{
            transform: isRolling ? "translateY(-100%)" : "translateY(0%)",
            opacity: isRolling ? 0 : 1,
            transition: hasTransition
              ? "transform 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease"
              : "none",
          }}
        >
          {roles[index]}
        </span>

        {/* Next word — rolls up from below and fades in */}
        <span
          className="absolute inset-0 flex items-center font-serif italic font-bold text-black dark:text-white underline decoration-1 underline-offset-4 whitespace-nowrap"
          style={{
            transform: isRolling ? "translateY(0%)" : "translateY(100%)",
            opacity: isRolling ? 1 : 0,
            transition: hasTransition
              ? "transform 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease 0.15s"
              : "none",
          }}
        >
          {roles[nextIndex]}
        </span>
      </span>
    </p>
  );
}

export default RotatingTagline;
